import moment from 'moment-timezone';

/**
 * What the organization is, for the modules that format on its behalf.
 *
 * Its clock, its language, its currency, its country: the handful of answers a
 * number, a date or a phone number has to know before it can be written down.
 *
 * Pushed in, never fetched. The readers of these values — the date and number
 * filters, the wall-clock util — are leaves that every screen imports, and one
 * of them is imported at boot, before Pinia exists, and by unit specs that run
 * without the bundler's aliases. Reaching for the store from there cannot work:
 * the code that tried it did so inside a `try/catch` and silently lost every
 * time, which is why an organization's own locale and currency never reached a
 * single figure on screen.
 *
 * So the organization store calls {@see setOrgPreferences} when it loads an
 * organization, and {@see clearOrgPreferences} on the way out.
 */
const preferences = {
    currency: null,
    country: null,
    timezone: null,
    estimateHoursPerDay: null,
    estimateDaysPerWeek: null
};

/**
 * @param {{currency?: string, country?: string, timezone?: string, estimateHoursPerDay?: number, estimateDaysPerWeek?: number}} values
 */
export function setOrgPreferences({ currency, country, timezone, estimateHoursPerDay, estimateDaysPerWeek } = {}) {
    preferences.currency = currency || null;
    preferences.country = country ? String(country).toUpperCase() : null;
    // An unrecognised zone is no zone: better the reader's own clock than a
    // silent wrong one.
    preferences.timezone = timezone && moment.tz.zone(timezone) ? timezone : null;
    preferences.estimateHoursPerDay = positiveOrNull(estimateHoursPerDay);
    preferences.estimateDaysPerWeek = positiveOrNull(estimateDaysPerWeek);
}

function positiveOrNull(value) {
    const number = Number(value);

    return Number.isFinite(number) && number > 0 ? number : null;
}

export function clearOrgPreferences() {
    setOrgPreferences({});
}

/**
 * The locale a number, a price or a date is written in: the language the reader
 * chose, in the country the organization works from — "1 234,50 $" for a French
 * reader of a Canadian shop, "$1,234.50" for an English one.
 *
 * The language belongs to the reader and can change without reloading the
 * organization, so it is read as late as possible rather than pushed in. The
 * country is the organization's and travels with it.
 *
 * A pair that Intl refuses is no locale at all: the fallback answers instead of
 * a formatter throwing halfway down a page of figures.
 */
export function orgLocale(fallback = 'en-CA') {
    const language = readerLanguage();

    if (!language) {
        return fallback;
    }

    const tag = preferences.country ? `${language}-${preferences.country}` : language;

    try {
        return Intl.NumberFormat.supportedLocalesOf(tag).length ? tag : fallback;
    } catch {
        return fallback;
    }
}

/** The UI language, from the app's global i18n — absent in a plain node spec. */
function readerLanguage() {
    if (typeof i18n === 'undefined') {
        return null;
    }

    const language = i18n?.locale?.value ?? i18n?.locale;

    return typeof language === 'string' && language ? language : null;
}

export function orgCurrency(fallback = 'CAD') {
    return preferences.currency || fallback;
}

export function orgCountry(fallback = 'CA') {
    return preferences.country || fallback;
}

/** The organization's IANA zone, or null when it never chose one. */
export function orgTimezone() {
    return preferences.timezone;
}

/**
 * What « 2j » and « 1s » are worth here — the table the duration grammar needs
 * before it can turn a day into minutes (see utils/duration).
 *
 * Eight hours a day, five days a week unless the organization said otherwise.
 * Never zero: a day worth no hours would make « 1j » zero minutes, an estimate
 * that silently disappears.
 */
export function orgEstimateScale() {
    return {
        hoursPerDay: preferences.estimateHoursPerDay ?? 8,
        daysPerWeek: preferences.estimateDaysPerWeek ?? 5
    };
}
